import { MetaLayout } from "@/common/MetaLayout";
import DatasetCard from "@/components/DatasetCard";
import { DatasetsEmpty } from "@/components/DatasetsEmpty";
import LoadMore from "@/components/LoadMore";
import { defineMessage, useIntl } from "react-intl";
import { Environment, graphql, usePaginationFragment } from "react-relay";
import { EntityDatasetsPageFragment$key } from "./__generated__/EntityDatasetsPageFragment.graphql";
import ErrorPage from "@/pages/_error";
import { preload, usePreloaded } from "@/utils/relay";
import { LoaderArgs } from "@/utils/route";
import { EntityDatasetsPageQuery as EntityDatasetsPageQueryType } from "./__generated__/EntityDatasetsPageQuery.graphql";
const Fragment = graphql`
  fragment EntityDatasetsPageFragment on Entity
  @refetchable(queryName: "EntityDatasetsPageFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
  ) {
    datasets(first: $count, after: $cursor)
      @connection(key: "EntityDatasetsPageFragment_datasets") {
      edges {
        node {
          id
          ...DatasetCardFragment
        }
      }
    }
  }
`;
export default function EntityDatasetsPage() {
  const intl = useIntl();
  const {
    query: { entityByUsername, viewer },
    variables,
  } = useEntityDatasetsPageQuery();
  const {
    data: { datasets },
    loadNext,
    hasNext,
    isLoadingNext,
  } = usePaginationFragment(
    Fragment,
    entityByUsername as EntityDatasetsPageFragment$key,
  );
  if (!entityByUsername) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage(
          { defaultMessage: "@{username} not found" },
          { username: variables.username },
        )}
      />
    );
  }
  if (!datasets.edges.length) {
    return <DatasetsEmpty />;
  }
  return (
    <MetaLayout
      hideTitleTemplate
      metaTitle={
        viewer && viewer.id === entityByUsername?.id
          ? intl.formatMessage({ defaultMessage: "My Datasets" })
          : undefined
      }
      metaDescription={
        viewer && viewer.id === entityByUsername?.id
          ? intl.formatMessage({
              defaultMessage:
                "View and manage all datasets you've created or contributed to on Aqora.",
            })
          : undefined
      }
    >
      <div className="grid xl:grid-cols-2 grid-cols-1 gap-6">
        {datasets.edges.map(({ node }) => (
          <DatasetCard dataset={node} key={node.id} />
        ))}

        <LoadMore
          loadMore={loadNext}
          hasMore={hasNext}
          isLoading={isLoadingNext}
        />
      </div>
    </MetaLayout>
  );
}
const EntityDatasetsPageQuery = graphql`
  query EntityDatasetsPageQuery($username: String!) {
    viewer @ifAllowed {
      id
    }
    entityByUsername(username: $username) {
      id
      username
      ...EntityDatasetsPageFragment
    }
  }
`;
export function loader(environment: Environment) {
  return ({ params: { username } }: LoaderArgs) => {
    if (!username) {
      throw new Response("No username", { status: 500 });
    }
    return preload<EntityDatasetsPageQueryType>(
      environment,
      EntityDatasetsPageQuery,
      { username },
    );
  };
}
const useEntityDatasetsPageQuery = () =>
  usePreloaded<EntityDatasetsPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Datasets" }),
};
